import nacl from 'tweetnacl'
import bs58 from 'bs58'

/**
 * Wallet signature verification utilities
 * Used for secure wallet authentication with signed disclaimers
 */

// Disclaimer version - increment this when the disclaimer text changes
// This will force all users to re-sign
export const DISCLAIMER_VERSION = 'v1'

/**
 * Generate disclaimer message to sign
 * Note: Message is deterministic (no timestamp) so we can verify version
 * @param {string} walletAddress - Wallet public key
 * @returns {string} Message to sign
 */
export function generateDisclaimerMessage(walletAddress) {
  const domain = typeof window !== 'undefined' ? window.location.host : 'pythentity.app'

  return `Welcome to Pythentity Initiative!

By signing this message, you agree to:
- Link your wallet to Pythentity
- Allow us to verify your Pythenians NFT ownership
- Store your Discord and Twitter linked accounts
- Calculate and display your reputation score

Wallet: ${walletAddress}
Domain: ${domain}
Disclaimer Version: ${DISCLAIMER_VERSION}

This signature will not trigger any blockchain transaction or cost any gas fees.`
}

/**
 * Request wallet signature for disclaimer
 * @param {object} wallet - Solana wallet adapter instance
 * @param {string} walletAddress - Wallet public key string
 * @returns {Promise<{signature: string, message: string, version: string}>}
 */
export async function requestWalletSignature(wallet, walletAddress) {
  const message = generateDisclaimerMessage(walletAddress)
  const encodedMessage = new TextEncoder().encode(message)

  try {
    // Request signature from wallet
    const result = await wallet.signMessage(encodedMessage)

    // Normalize: some adapters (e.g. Phantom) return raw Uint8Array,
    // others (e.g. Backpack) return { signature: Uint8Array }
    const signatureBytes = result instanceof Uint8Array ? result : result.signature

    // Convert signature to base58 string for storage
    const signatureBase58 = bs58.encode(signatureBytes)

    return {
      signature: signatureBase58,
      message,
      version: DISCLAIMER_VERSION,
      signedAt: new Date().toISOString()
    }
  } catch (error) {
    throw new Error('User rejected signature request')
  }
}

/**
 * Verify wallet signature
 * @param {string} signatureBase58 - Signature as base58 string
 * @param {string} message - Original message that was signed
 * @param {string} publicKeyString - Wallet public key as string
 * @returns {boolean} True if signature is valid
 */
export function verifySignature(signatureBase58, message, publicKeyString) {
  try {
    const encodedMessage = new TextEncoder().encode(message)
    const signatureBytes = bs58.decode(signatureBase58)
    const publicKeyBytes = bs58.decode(publicKeyString)

    return nacl.sign.detached.verify(
      encodedMessage,
      signatureBytes,
      publicKeyBytes
    )
  } catch (error) {
    return false
  }
}

/**
 * Check if user needs to sign disclaimer
 * Compares stored version with current version
 * @param {object} existingUser - User object from database (may be null)
 * @returns {boolean} True if signature is needed
 */
export function needsSignature(existingUser) {
  if (!existingUser) {
    return true // New user, needs signature
  }

  if (!existingUser.disclaimerSignature) {
    return true // No signature stored
  }

  if (existingUser.disclaimerSignature.version !== DISCLAIMER_VERSION) {
    return true // Version changed, needs re-sign
  }

  return false // Has valid signature for current version
}

/**
 * DEPRECATED: localStorage functions kept for backwards compatibility
 * These will be removed in a future version
 */

const STORAGE_KEYS = {
  SIGNED_WALLETS: 'pythentity_signed_wallets',
  LAST_SIGNATURE: 'pythentity_last_signature'
}

/**
 * @deprecated Use needsSignature(existingUser) instead
 */
export function hasWalletSigned(walletAddress) {
  try {
    const signedWallets = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SIGNED_WALLETS) || '{}'
    )
    return !!signedWallets[walletAddress]
  } catch (err) {
    return false
  }
}

/**
 * @deprecated Signatures should be stored in MongoDB
 */
export function storeWalletSignature(walletAddress, signatureData) {
  try {
    const signedWallets = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SIGNED_WALLETS) || '{}'
    )

    signedWallets[walletAddress] = {
      signature: signatureData.signature,
      message: signatureData.message,
      version: signatureData.version || DISCLAIMER_VERSION,
      signedAt: signatureData.signedAt || new Date().toISOString()
    }

    localStorage.setItem(STORAGE_KEYS.SIGNED_WALLETS, JSON.stringify(signedWallets))
    localStorage.setItem(STORAGE_KEYS.LAST_SIGNATURE, JSON.stringify({
      wallet: walletAddress,
      ...signedWallets[walletAddress]
    }))
  } catch (err) {
    // Failed to store signature
  }
}

/**
 * @deprecated Use database lookup instead
 */
export function getStoredSignature(walletAddress) {
  try {
    const signedWallets = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SIGNED_WALLETS) || '{}'
    )
    return signedWallets[walletAddress] || null
  } catch (err) {
    return null
  }
}

/**
 * Clear signature data from localStorage
 * @param {string} walletAddress - Optional: Clear specific wallet, or all if not provided
 */
export function clearSignature(walletAddress = null) {
  try {
    if (walletAddress) {
      const signedWallets = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.SIGNED_WALLETS) || '{}'
      )
      delete signedWallets[walletAddress]
      localStorage.setItem(STORAGE_KEYS.SIGNED_WALLETS, JSON.stringify(signedWallets))
    } else {
      localStorage.removeItem(STORAGE_KEYS.SIGNED_WALLETS)
      localStorage.removeItem(STORAGE_KEYS.LAST_SIGNATURE)
    }
  } catch (err) {
    // Failed to clear signature
  }
}
